import { useEffect, useState } from "react";
import { Box, Drawer, DrawerContent, DrawerOverlay } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { ColorIcon, customEvent, HackleEventName, Spacing } from "goi_common";
import { BaseStack, baseColors } from "goi_common/src/design-system";

import { 장례준비긴급도_LIST } from "@/containers/MatchContainer/constants/request";
import type { E5ReceiveMethod } from "../utils/e5Receive";

/**
 * value 는 견적 body(status)에 그대로 나가는 기존 값(장례준비긴급도_LIST). 순서는 기존 폼과 같은 여유 → 급함.
 * 질문 · 선택지는 기존 폼의 장례 준비 상황 문항을 살렸다. 「임종」 을 바로 쓰는 쪽이 가장 빨리 읽힌다(2026-11-04 주완).
 */
export const TIMING_OPTIONS = [
  // 기존 폼(「장례 준비 상황을 자세히 알려주세요」) 문구가 가장 직관적이라 그 뼈대를 살려 해요체 한 줄로 다듬었다(2026-11-04 주완)
  // gives: 고르면 그 아래 열리는 「이렇게 정리해 드려요」. 고르는 이유를 고르는 순간 보여 준다(안 2).
  // 답마다 말이 맞게, 급할수록 「~부터」 가 앞당겨지게: 미리 알아 둘 비용 → 아낄 수 있는 것부터 → 지금 정해야 할 것부터 → 바로 필요한 것부터.
  // 이미 한 장례식장을 보고 있는 사람이라 「장례식장을 비교」 는 쓰지 않는다(2026-11-04 주완)
  // 상황별 알림톡 순서(crm_v2 status_*)도 실제로 이 답에 따라 달라진다
  {
    value: 장례준비긴급도_LIST[0],
    title: "급하지 않아요, 미리 알아보는 중이에요",
    gives: "미리 준비할 것을 알려 드려요",
  },
  {
    value: 장례준비긴급도_LIST[1],
    title: "1주에서 한 달 정도 남은 것 같아요",
    gives: "아낄 수 있는 것부터 알려 드려요",
  },
  {
    value: 장례준비긴급도_LIST[2],
    title: "임종이 며칠 남지 않았어요",
    gives: "지금 정할 것부터 알려 드려요",
  },
  {
    value: 장례준비긴급도_LIST[3],
    title: "임종하신 상태예요",
    gives: "바로 할 일부터 알려 드려요",
  },
];

/**
 * E5 비용 구성 아래 CTA 를 누르면 그 화면 위에 열리는 바텀시트. 준비 시간(필수, 라디오)을 고르고
 * 이 시트 안에서 바로 받는 방법(카카오톡 / 카카오톡 없이)을 누른다. 화면을 하나 더 거치지 않는다(2026-11-04 주완).
 * 새 화면으로 넘기면 위 비용 카드가 또 나오고 질문이 하나 더 붙은 것처럼 보여서 시트로 뺐다.
 * #130 에서 이 질문을 맨 앞에 두면 견적 제출이 28.12% → 13.58% 로 떨어져, 받기 직전 자리를 지킨다.
 */
export default function TimingSheet({
  isOpen,
  onClose,
  loading,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  loading: boolean;
  /** 고른 준비 시간과 받는 방법. 저장 · 이동은 부르는 쪽이 한다 */
  onSubmit: (status: string, method: E5ReceiveMethod) => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  // 열 때마다 빈 상태로. 시트는 닫혀도 사라지지 않아 전에 고른 답이 미리 골라진 채로 보였다(2026-11-04 주완)
  useEffect(() => {
    if (isOpen) setSelected(null);
  }, [isOpen]);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="bottom">
      <DrawerOverlay />
      {/* Chakra Drawer 기본 너비 제한을 풀고, 안쪽을 480px 가운데로 */}
      <DrawerContent
        bg="transparent"
        boxShadow="none"
        // 앱 전역 테마가 Drawer 너비를 덮어써 !important 로 푼다(DormantDrawer 와 같은 방식)
        sx={{ width: "100% !important", maxWidth: "100% !important" }}
        alignItems="center"
      >
        <BaseStack
          width="100%"
          maxW="480px"
          bg={baseColors.background_normal}
          borderRadius="20px 20px 0 0"
          px="20px"
          pt="12px"
          pb="calc(16px + env(safe-area-inset-bottom))"
        >
          {/* 손잡이. 아래로 내려 닫을 수 있는 시트라는 표시 */}
          <Box
            alignSelf="center"
            width="36px"
            height="4px"
            borderRadius="999px"
            bg={baseColors.fill_accent}
          />
          <Spacing gap={20} />
          {/* 「맞춤」 이 아래 상황별 상자와 이어지고, 버튼도 「맞춤 견적 받기」 로 맞춘다. 제목은 21px(headline_1).
              버튼에 절약 · 혜택을 붙인 실험(#79 차이 없음, #37 가입 4.11% → 1.22%)이 있어 버튼은 받는 것만 말한다(2026-11-04 주완) */}
          {/* 할 일(상황 고르기) → 받는 것(맞춤 견적) 순서로 「이걸 주면 이걸 드린다」. 버튼 「맞춤 견적 받기」 와 같은 말(2026-11-05 주완) */}
          <Box
            as="h2"
            className="headline_1_sb"
            color={baseColors.label_normal}
            wordBreak="keep-all"
          >
            장례 준비 상황을 골라 주시면
            <br />
            맞춤 견적을 보내 드릴게요
          </Box>
          <Spacing gap={12} />

          {/* 하나만 고르는 필수 질문이라 라디오 */}
          <BaseStack width="100%" role="radiogroup" aria-label="장례 준비 상황">
            {TIMING_OPTIONS.map((o) => {
              const on = selected === o.value;
              return (
                <Box
                  key={o.value}
                  as="button"
                  type="button"
                  role="radio"
                  aria-checked={on}
                  width="100%"
                  py="16px"
                  display="flex"
                  alignItems="flex-start"
                  textAlign="left"
                  cursor="pointer"
                  onClick={() => {
                    setSelected(o.value);
                    // 기존 폼과 같은 선택 이벤트(답 분포 · Hackle 지표 연속성)
                    customEvent({
                      newGtm: { path: "parlor_match_request", trigger_and_target: "status-answer" },
                    });
                    customEvent({
                      hackle: {
                        key: HackleEventName["견적 요청 - 장례준비긴급도 선택"],
                        properties: { name: o.value },
                      },
                    });
                  }}
                >
                  <RadioMark on={on} />
                  <Spacing gap={12} orientation="horizontal" />
                  <Box flex={1} minW={0}>
                    <Box
                      className={on ? "body_1_normal_sb" : "body_1_normal_m"}
                      color={baseColors.label_normal}
                    >
                      {o.title}
                    </Box>
                    {/* 고르면 그 아래로 받을 내용이 열린다. 하나만 열린다 */}
                    <AnimatePresence initial={false}>
                      {on && (
                        <motion.div
                          key="gives"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                          style={{ overflow: "hidden" }}
                        >
                          {/* B안: 상자 없이 회색 작은 글씨 한 줄. 제목이 이유를 말하니 여기는 눈에 덜 띄게(2026-11-04 주완) */}
                          <Box
                            pt="4px"
                            className="label_1_r"
                            color={baseColors.label_assistive_1}
                            wordBreak="keep-all"
                          >
                            {o.gives}
                          </Box>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Box>
                </Box>
              );
            })}
          </BaseStack>

          <Spacing gap={16} />
          {/* 버튼은 실제 순서대로: 로그인(또는 번호 입력) → 견적. 「카톡으로 받기」 · 「바로 확인하기」 는 카톡으로 오거나
              이 자리에서 바로 보이는 것처럼 읽혀 기대가 어긋난다(유효율 하락 우려, 2026-11-04 주완).
              상담 전화 안내는 리드 제출을 막지 않도록 여기 두지 않고 로그인 뒤 화면에서 한다 */}
          {/* 확인 방법. 준비 시간을 고르기 전에는 누를 수 없다 */}
          <Box
            as="button"
            type="button"
            className="body_1_normal_m"
            width="100%"
            height="52px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="6px"
            // 카카오 브랜드 버튼은 디자인 시스템 BaseButton 타입에 없어 로그인 화면과 같은 방식으로 그린다
            backgroundColor="#fee502"
            color={baseColors.label_normal}
            opacity={selected === null ? 0.4 : 1}
            cursor={selected === null || loading ? "default" : "pointer"}
            disabled={selected === null || loading}
            onClick={() => selected && onSubmit(selected, "kakao")}
          >
            {/* 카카오 심볼(벡터). CDN kakao.svg 는 24px PNG 라 흐리게 보여 쓰지 않는다 */}
            <ColorIcon name="icon_kakao_symbol" size={20} color={baseColors.label_normal} />
            <Spacing gap={6} orientation="horizontal" />
            {loading ? "잠시만요" : "카카오로 로그인하고 맞춤 견적 받기"}
          </Box>
          <Box
            as="button"
            type="button"
            className="body_1_normal_m"
            width="100%"
            height="48px"
            color={baseColors.label_alternative_1}
            opacity={selected === null ? 0.4 : 1}
            cursor={selected === null || loading ? "default" : "pointer"}
            disabled={selected === null || loading}
            onClick={() => selected && onSubmit(selected, "nokakao")}
          >
            휴대폰 번호로 맞춤 견적 받기
          </Box>
        </BaseStack>
      </DrawerContent>
    </Drawer>
  );
}

/** 라디오 표시. 고르면 주황 링 안에 점 */
const RadioMark = ({ on }: { on: boolean }) => (
  <Box
    width="22px"
    height="22px"
    flexShrink={0}
    borderRadius="999px"
    border={`2px solid ${on ? baseColors.primary_orange : baseColors.line_accent}`}
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    {on && <Box width="10px" height="10px" borderRadius="999px" bg={baseColors.primary_orange} />}
  </Box>
);
